import { getChartColorForDevice } from '@/components/charts/color_helpers'
import i18n from '@/i18n'

export const SENSOR_TYPE_FLAG_VALUES = {
  flowIntoSystem: 1,
  flowFromSystem: 2,
  producer: 4,
  consumer: 8,
  electricEnergy: 16,
  electricPower: 32,
  thermal: 64,
  thermalEnergy: 128,
  thermalPower: 256,
  abstract: 512
}

const TYPE_ORDER = {
  user: 1,
  pv: 2,
  chp: 3,
  fuelcell: 4,
  wind: 5,
  heatpump: 6,
  wallbox: 7,
  heatingelement: 8,
  battery: 9,
  prod: 10,
  cons: 11,
  grid: 12
}

/**
 * Returns a full balance hierarchy tree consisting of Node's
 *
 * the full structure is:
 *
 * {
 *   prodTree => prod     => deviceType => singleDevice
 *            => fromGrid => singleGrids
 *
 *   batDmdTree => batteryDemand => singleBatterieDemands
 *
 *   batSupTree => batterySupply => singleBatterieSupplies
 *
 *   consTree => cons   => deviceType => singleDevice
 *            => toGrid => [singleGrids]
 * }
 *
 * @function
 *
 * @params {object} sensors is a map of {sensorApiKey => Sensor}
 *
 * @return {object} the build sensorTree
 */

export function buildInitTree(sensors) {
  const t = i18n.global.t
  const structure = { prodTree: {}, batDmdTree: {}, batSupTree: {}, consTree: {} }
  for (const apiKey in sensors) {
    const sensor = sensors[apiKey]
    const deviceType = sensor.deviceType
    const sensorTypeFlag = sensor.sensorTypeFlag
    const unit = sensor.unit

    if (deviceType === 'battery') {
      // is Battery
      if (isIntoSystem(sensor)) {
        // is Battery supply
        if (Object.keys(structure.batSupTree).length === 0) {
          //main Tree not initialized yet
          structure.batSupTree = new Node({
            label: t('api.energy_balance.battery_supply'),
            deviceType,
            apiKey: 'batterySupply',
            sensorTypeFlag,
            unit
          })
        }
        structure.batSupTree.children.push(new Node(sensor))
      } else if (isFromSystem(sensor)) {
        // is Battery demand
        if (Object.keys(structure.batDmdTree).length === 0) {
          //main Tree not initialized yet
          structure.batDmdTree = new Node({
            label: t('api.energy_balance.battery_demand'),
            deviceType,
            apiKey: 'batteryDemand',
            sensorTypeFlag,
            unit
          })
        }
        structure.batDmdTree.children.push(new Node(sensor))
      }
    } else if (isIntoSystem(sensor)) {
      // is into System
      if (Object.keys(structure.prodTree).length === 0) {
        //main Tree not initialized yet
        structure.prodTree = new Node({
          label: t('api.energy_balance.prod_and_grid'),
          deviceType: 'prodAndGrid',
          apiKey: 'prodAndGrid',
          sensorTypeFlag,
          unit
        })
      }
      let subtree = structure.prodTree

      if (isProd(sensor)) {
        // is Producer
        let existingIndex = getSubtreeIndex(subtree, 'prod')
        if (existingIndex === -1) {
          //prod Tree not initialized yet
          subtree.children.push(
            new Node({
              label: t('api.energy_balance.prod'),
              deviceType: 'prod',
              apiKey: 'prod',
              sensorTypeFlag,
              unit
            })
          )
          existingIndex = getSubtreeIndex(subtree, 'prod')
        }

        subtree = subtree.children[existingIndex]

        existingIndex = getSubtreeIndex(subtree, deviceType + 'Supply')
        if (existingIndex === -1) {
          //Device Tree not initialized yet
          subtree.children.push(
            new Node({
              label: t('api.devices.' + deviceType),
              deviceType,
              apiKey: deviceType + 'Supply',
              sensorTypeFlag,
              unit
            })
          )
          existingIndex = getSubtreeIndex(subtree, deviceType + 'Supply')
        }

        subtree = subtree.children[existingIndex]
        subtree.children.push(new Node(sensor))
      } else if (deviceType === 'grid') {
        // is Grid
        let existingIndex = getSubtreeIndex(subtree, 'From' + deviceType)
        if (existingIndex === -1) {
          //Grid Tree not initialized yet
          subtree.children.push(
            new Node({
              label: t('api.energy_balance.from_grid'),
              deviceType,
              apiKey: 'From' + deviceType,
              sensorTypeFlag,
              unit
            })
          )
          existingIndex = getSubtreeIndex(subtree, 'From' + deviceType)
        }

        subtree = subtree.children[existingIndex]

        subtree.children.push(new Node(sensor))
      }
    } else if (isFromSystem(sensor)) {
      // is from System

      if (Object.keys(structure.consTree).length === 0) {
        //main Tree not initialized yet
        structure.consTree = new Node({
          label: t('api.energy_balance.cons_and_grid'),
          deviceType: 'consAndGrid',
          apiKey: 'consAndGrid',
          sensorTypeFlag,
          unit
        })
      }
      let subtree = structure.consTree

      if (isCons(sensor)) {
        // is Consumer
        let existingIndex = getSubtreeIndex(subtree, 'cons')
        if (existingIndex === -1) {
          //prod Tree not initialized yet
          subtree.children.push(
            new Node({
              label: t('api.energy_balance.cons'),
              deviceType: 'cons',
              apiKey: 'cons',
              sensorTypeFlag,
              unit
            })
          )
          existingIndex = getSubtreeIndex(subtree, 'cons')
        }

        subtree = subtree.children[existingIndex]

        existingIndex = getSubtreeIndex(subtree, deviceType + 'Demand')
        if (existingIndex === -1) {
          //Device Tree not initialized yet
          subtree.children.push(
            new Node({
              label: t('api.devices.' + deviceType),
              deviceType,
              apiKey: deviceType + 'Demand',
              sensorTypeFlag,
              unit
            })
          )
          existingIndex = getSubtreeIndex(subtree, deviceType + 'Demand')
        }

        subtree = subtree.children[existingIndex]
        subtree.children.push(new Node(sensor))
      } else if (deviceType === 'grid') {
        // is Grid
        let existingIndex = getSubtreeIndex(subtree, 'To' + deviceType)
        if (existingIndex === -1) {
          //Grid Tree not initialized yet
          subtree.children.push(
            new Node({
              label: t('api.energy_balance.to_grid'),
              deviceType,
              apiKey: 'To' + deviceType,
              sensorTypeFlag,
              unit
            })
          )
          existingIndex = getSubtreeIndex(subtree, 'To' + deviceType)
        }

        subtree = subtree.children[existingIndex]

        subtree.children.push(new Node(sensor))
      }
    }
  }
  return structure
}

/**
 * Collapses a Tree by reducing subtrees with single children
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (each tree individually)
 *
 * @return {object} the reduced tree
 */

export function collapseTree(tree) {
  if (!tree.children || !tree.children.length) {
    return tree
  }

  const newChildren = []
  for (const child of tree.children) {
    newChildren.push(collapseTree(child))
  }
  tree.children = newChildren

  if (tree.children.length === 1) {
    return tree.children[0]
  }
  return tree
}

/**
 * adds a valKeys String[] to all tree nodes that consists of either the own apiKey, or of all the leaf nodes under the node
 *
 * additionally adds the level and invLevel to each node
 *
 * sorts all children in each node by TYPE_ORDER
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (each tree individually)
 *
 * @params {integer} level, number showing the depth of the treenode. is increased with each step down
 *
 * @params {integer} invLevel, number showing the invLevel of the treenode. is reduced with each step down and initialized with the max depth of the tree
 *
 * @return {object} the enriched tree
 */

export function addTreeKeyDataAndLevel(tree, level, invLevel) {
  if (!Object.keys(tree).length) {
    return tree
  }

  tree.invLevel = invLevel
  tree.level = level

  if (!tree.children || !tree.children.length) {
    tree.valKeys = [tree.apiKey]
    return tree
  }
  let valKeys = []
  const newChildren = []

  for (const child of tree.children) {
    const newChild = addTreeKeyDataAndLevel(child, level + 1, invLevel - 1)
    newChildren.push(newChild)
    valKeys = valKeys.concat(newChild.valKeys)
  }
  tree.children = newChildren.sort(function (a, b) {
    //sort children by deviceType priority (small to large)
    const keyA = TYPE_ORDER[a.type],
      keyB = TYPE_ORDER[b.type]
    if (keyA < keyB) return -1
    if (keyA > keyB) return 1
    if (a.apiKey < b.apiKey) return -1
    if (a.apiKey > b.apiKey) return 1

    return 0
  })
  tree.valKeys = valKeys
  return tree
}

/**
 * returns the maximum depth of the tree
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (each tree individually)
 *
 * @params {integer} current is the initial depth (usually 0)
 *
 * @return {integer} the maximum depth
 */
export function getMaxDepth(tree, current = 0) {
  if (!tree.children || !tree.children.length) {
    return current
  }

  let maxChildren = 0
  for (const child of tree.children) {
    const maxChild = getMaxDepth(child, current + 1)
    maxChildren = maxChild > maxChildren ? maxChild : maxChildren
  }
  return maxChildren
}

/**
 * helperfunction to get the index of a child in a tree, based on the apiKey
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (each tree individually)
 *
 * @params {string} apikey is the searched apiKey
 *
 * @return {integer} index for find, -1 if the apiKey is not present
 */
function getSubtreeIndex(tree, apiKey) {
  if (!tree.children || !tree.children.length) {
    return -1
  }

  for (let i = 0; i < tree.children.length; i++) {
    if (tree.children[i].apiKey === apiKey) {
      return i
    }
  }
  return -1
}

/**
 * helperfunction to create the nodes information for a sankey from a tree
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (each subTree individually)
 *
 * @params {string} useAsLevel, chooses treeattribute to use as level ['level' | 'invLevel']
 *
 * @params {integer} levelOffset, optional offset added to the level
 *
 * @return {object}[] every node consists of { id => apiKey, level, name => label, color, offset[optional] }
 */
export function createSankeyNodeInformation(tree, useAsLevel, levelOffset = 0) {
  if (!Object.keys(tree).length) {
    return []
  }

  let allNodes = []

  for (const child of tree.children) {
    allNodes = allNodes.concat(createSankeyNodeInformation(child, useAsLevel, levelOffset))
  }

  allNodes.push({
    id: tree.apiKey,
    level: tree[useAsLevel] + levelOffset,
    name: tree.label,
    color: tree.color,
    unit: tree.unit
  })

  return allNodes
}

/**
 * helperfunction to create the battery nodes information for a sankey from a tree
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (full structure!!!)
 *
 * @params {integer} levelOffset, optional offset added to the level
 *
 * @params {integer} offsetVertical, optional vertical offset of each node
 *
 * @return {object}[] every node consists of { id => apiKey, level, name => label, color, offset[optional] }
 */
export function createSankeyNodeInformationMergedBatteries(tree, levelOffset = 0, offsetVertical = 0, flipped = false) {
  if (tree.batDmdTree.children.length === 0 && tree.batSupTree.children.length === 0) {
    return [
      {
        id: 'battery',
        level: tree.batDmdTree.level + levelOffset,
        name: tree.batDmdTree.label,
        color: tree.batDmdTree.color,
        offset: offsetVertical,
        unit: tree.batDmdTree.unit
      }
    ]
  }

  let demNodes, supNodes

  if (flipped) {
    supNodes = createSankeyNodesForMergedBatteries(tree.batSupTree, 'level', levelOffset, 'gen', offsetVertical)

    demNodes = createSankeyNodesForMergedBatteries(
      tree.batDmdTree,
      'invLevel',
      supNodes.length === 1 ? levelOffset : levelOffset + 1,
      'con',
      offsetVertical
    )
  } else {
    demNodes = createSankeyNodesForMergedBatteries(tree.batDmdTree, 'level', levelOffset, 'con', offsetVertical)

    supNodes = createSankeyNodesForMergedBatteries(
      tree.batSupTree,
      'invLevel',
      demNodes.length === 1 ? levelOffset : levelOffset + 1,
      'gen',
      offsetVertical
    )
  }

  return filterDuplicatedNodes(demNodes.concat(supNodes))
}

function createSankeyNodesForMergedBatteries(tree, useAsLevel, levelOffset, deviceClass, offsetVertical) {
  let allNodes = []

  for (const child of tree.children) {
    allNodes = allNodes.concat(
      createSankeyNodesForMergedBatteries(child, useAsLevel, levelOffset, deviceClass, offsetVertical)
    )
  }

  if (tree.countId) {
    allNodes.push({
      id: tree.type + tree.countId,
      level: tree[useAsLevel] + levelOffset,
      name: tree.label,
      color: tree.color,
      offset: offsetVertical,
      unit: tree.unit
    })
  } else {
    allNodes.push({
      id: tree.apiKey,
      level: tree[useAsLevel] + levelOffset,
      name: tree.label,
      color: tree.color,
      offset: offsetVertical,
      unit: tree.unit
    })
  }

  return allNodes
}

function filterDuplicatedNodes(nodes) {
  const returnNodes = []
  const seenIds = []
  for (const node of nodes) {
    if (seenIds.includes(node.id)) {
      continue
    }
    returnNodes.push(node)
    seenIds.push(node.id)
  }
  return returnNodes
}

/**
 * helperfunction to create the nodes information for a sankey from a tree for the full flow: production <=> battery <=> consumption
 *
 * @function
 *
 * @params {object} tree, see buildInitTree (full structure!!!)
 *
 * @return {object}[] every node consists of { id => apiKey, level, name => label, color, offsetVerical[optional] }
 */
export function createSankeyNodeInformationForFullTree(tree, flipped = false) {
  let nodes = []
  let levelOffset = 0

  if (!tree.prodTree) {
    return []
  }

  if (flipped) {
    nodes = nodes.concat(createSankeyNodeInformation(tree.consTree, 'invLevel', levelOffset))

    levelOffset += getMaxDepth(tree.consTree, 1)

    if (Object.keys(tree.batDmdTree).length) {
      nodes = nodes.concat(createSankeyNodeInformationMergedBatteries(tree, levelOffset, 100, flipped))

      levelOffset += getMaxDepth(tree.batDmdTree, 1) * 2 - 1
    }

    nodes = nodes.concat(createSankeyNodeInformation(tree.prodTree, 'level', levelOffset))
  } else {
    nodes = nodes.concat(createSankeyNodeInformation(tree.prodTree, 'invLevel', levelOffset))

    levelOffset += getMaxDepth(tree.prodTree, 1)

    if (Object.keys(tree.batDmdTree).length) {
      nodes = nodes.concat(createSankeyNodeInformationMergedBatteries(tree, levelOffset, 100, flipped))

      levelOffset += getMaxDepth(tree.batDmdTree, 1) * 2 - 1
    }

    nodes = nodes.concat(createSankeyNodeInformation(tree.consTree, 'level', levelOffset))
  }
  return nodes
}

/**
 * helperfunction to create the series information for a sankey from a tree by traversing it in order
 *
 * @function
 *
 * @params {object} vals, is a mapping {string}apiKey => {integer}val
 *
 * @params {object} tree, see buildInitTree (each tree individually)
 *
 * @params {string} direction of the created flow, 'bottomUp | topDown'
 *
 * @return {object}[] every series point consists of { {string}from, {string}to, {float}weight, {string}color }
 */
export function createSankeySeriesInorder(vals, tree, direction = 'bottomUp') {
  let series = []

  if (!Object.keys(tree).length) {
    return []
  }

  for (const child of tree.children) {
    if (direction === 'bottomUp') {
      series = series.concat(createSankeySeriesInorder(vals, child, direction))
      series.push({
        from: child.apiKey,
        to: tree.apiKey,
        weight: valFunction(vals, child.valKeys),
        color: child.color,
        visible: valFunction(vals, child.valKeys) > 0,
        unit: child.unit
      })
    } else if (direction === 'topDown') {
      series.push({
        from: tree.apiKey,
        to: child.apiKey,
        weight: valFunction(vals, child.valKeys),
        color: child.color,
        visible: valFunction(vals, child.valKeys) > 0,
        unit: child.unit
      })
      series = series.concat(createSankeySeriesInorder(vals, child, direction))
    }
  }

  return series
}

/**
 * helperfunction to calculate the value of a list of keys
 *
 * @function
 *
 * @params {object} vals, is a mapping {string}apiKey => {integer}val
 *
 * @params {string}[] keys, is a list of apiKeys
 *
 * @return {integer} returns the sum
 */
function valFunction(vals, keys = []) {
  let sum = 0
  for (const key of keys) {
    if (vals[key]) {
      sum += vals[key]
    }
  }
  return sum
}

/**
 * helperfunction to create the merged battery series information for a sankey diagramm from a tree by traversing it in order
 *
 * @function
 *
 * @params {object} vals, is a mapping {string}apiKey => {integer}val
 *
 * @params {object} tree, see buildInitTree (full Structure!!!)
 *
 * @return {object}[] every series point consists of { {string}from, {string}to, {float}weight, {string}color }
 */
export function createSankeySeriesMergedBatteries(vals, tree, flipped = false) {
  if (
    !Object.keys(tree.batDmdTree).length ||
    (tree.batDmdTree.children.length === 0 && tree.batSupTree.children.length === 0)
  ) {
    return []
  }

  const demSeries = createSankeyMergedBatterySeriesInorder(vals, tree.batDmdTree, flipped ? 'bottomUp' : 'topDown')
  const supSeries = createSankeyMergedBatterySeriesInorder(vals, tree.batSupTree, flipped ? 'topDown' : 'bottomUp')
  return demSeries.concat(supSeries)
}

function createSankeyMergedBatterySeriesInorder(vals, tree, direction = 'bottomUp') {
  let series = []
  if (!Object.keys(tree).length) {
    return series
  }

  for (const child of tree.children) {
    if (direction === 'bottomUp') {
      series = series.concat(createSankeySeriesInorder(vals, child, direction))
      series.push({
        from: child.type + child.countId,
        to: tree.apiKey,
        weight: valFunction(vals, child.valKeys),
        color: child.color,
        visible: valFunction(vals, child.valKeys) > 0,
        unit: child.unit
      })
    } else if (direction === 'topDown') {
      series.push({
        from: tree.apiKey,
        to: child.type + child.countId,
        weight: valFunction(vals, child.valKeys),
        color: child.color,
        visible: valFunction(vals, child.valKeys) > 0,
        unit: child.unit
      })
    }
    series = series.concat(createSankeySeriesInorder(vals, child, direction))
  }

  return series
}

/**
 * helperfunction to create the series information for a sankey diagramm from a tree for the full flow: producer <=> battery <=> consumer
 *
 * @function
 *
 * @params {object} vals, is a mapping {string}apiKey => {integer}val
 *
 * @params {object} tree, see buildInitTree (full Structure!!!)
 *
 * @return {object}[] every series point consists of { {string}from, {string}to, {float}weight, {string}color }
 */
export function createSankeySeriesForFullTree(vals, tree, flipped = false) {
  let series = []

  series = series.concat(createSankeySeriesInorder(vals, tree.prodTree, flipped ? 'topDown' : 'bottomUp'))

  series = series.concat(createSankeySeriesFullTreeConnectors(vals, tree, flipped))
  series = series.concat(createSankeySeriesMergedBatteries(vals, tree, flipped))

  series = series.concat(createSankeySeriesInorder(vals, tree.consTree, flipped ? 'bottomUp' : 'topDown'))

  return series
}

function createSankeySeriesFullTreeConnectors(vals, tree, flipped = false) {
  const series = []

  if (Object.keys(tree.batDmdTree).length) {
    const batterySup = tree.batSupTree.children.length ? tree.batSupTree.apiKey : 'battery'
    const batteryDem = tree.batDmdTree.children.length ? tree.batDmdTree.apiKey : 'battery'
    if (flipped) {
      series.push({
        from: tree.consTree.apiKey,
        to: tree.prodTree.apiKey,
        weight: valFunction(vals, tree.consTree.valKeys) - valFunction(vals, tree.batSupTree.valKeys),
        visible: valFunction(vals, tree.consTree.valKeys) - valFunction(vals, tree.batSupTree.valKeys) > 0,
        unit: tree.consTree.unit
      })

      series.push({
        from: tree.consTree.apiKey,
        to: batterySup,
        weight: valFunction(vals, tree.batSupTree.valKeys),
        color: tree.batSupTree.color,
        visible: valFunction(vals, tree.batSupTree.valKeys) > 0,
        unit: tree.consTree.unit
      })

      series.push({
        from: batteryDem,
        to: tree.prodTree.apiKey,
        weight: valFunction(vals, tree.batDmdTree.valKeys),
        color: tree.batDmdTree.color,
        visible: valFunction(vals, tree.batDmdTree.valKeys) > 0,
        unit: tree.prodTree.unit
      })
    } else {
      series.push({
        from: tree.prodTree.apiKey,
        to: tree.consTree.apiKey,
        weight: valFunction(vals, tree.prodTree.valKeys) - valFunction(vals, tree.batDmdTree.valKeys),
        visible: valFunction(vals, tree.prodTree.valKeys) - valFunction(vals, tree.batDmdTree.valKeys) > 0,
        unit: tree.consTree.unit
      })

      series.push({
        from: tree.prodTree.apiKey,
        to: batteryDem,
        weight: valFunction(vals, tree.batDmdTree.valKeys),
        color: tree.batDmdTree.color,
        visible: valFunction(vals, tree.batDmdTree.valKeys) > 0,
        unit: tree.prodTree.unit
      })

      series.push({
        from: batterySup,
        to: tree.consTree.apiKey,
        weight: valFunction(vals, tree.batSupTree.valKeys),
        color: tree.batSupTree.color,
        visible: valFunction(vals, tree.batSupTree.valKeys) > 0,
        unit: tree.consTree.unit
      })
    }
  } else {
    if (flipped) {
      series.push({
        from: tree.consTree.apiKey,
        to: tree.prodTree.apiKey,
        weight: valFunction(vals, tree.consTree.valKeys),
        visible: valFunction(vals, tree.consTree.valKeys) > 0,
        unit: tree.consTree.unit
      })
    } else {
      series.push({
        from: tree.prodTree.apiKey,
        to: tree.consTree.apiKey,
        weight: valFunction(vals, tree.prodTree.valKeys),
        visible: valFunction(vals, tree.prodTree.valKeys) > 0,
        unit: tree.consTree.unit
      })
    }
  }

  return series
}

/**
 * A Node data-struct.
 *
 * @constructor
 *
 * @param {Sensor} sensor
 */
export function Node(sensor) {
  const intoSystem = isIntoSystem(sensor)

  this.label = sensor.label
  this.type = sensor.deviceType
  this.color = getChartColorForDevice({
    deviceName: sensor.deviceType,
    deviceClass: intoSystem ? 'gen' : 'con',
    countId: sensor.countId
  })
  this.apiKey = sensor.apiKey
  this.unit = sensor.unit
  if (sensor.countId) {
    this.countId = sensor.countId
  }
  this.children = []
}

function isProd(sensor) {
  return (sensor.sensorTypeFlag & SENSOR_TYPE_FLAG_VALUES.producer) > 0
}

function isCons(sensor) {
  return (sensor.sensorTypeFlag & SENSOR_TYPE_FLAG_VALUES.consumer) > 0
}

function isIntoSystem(sensor) {
  return (sensor.sensorTypeFlag & SENSOR_TYPE_FLAG_VALUES.flowIntoSystem) > 0
}

function isFromSystem(sensor) {
  return (sensor.sensorTypeFlag & SENSOR_TYPE_FLAG_VALUES.flowFromSystem) > 0
}
